'use client';

import type { ForwardedRef } from 'react';
import { Label as RACLabel } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { InternalSpinner } from '../internal/spinner/spinner.js';
import type { CommonProps, LabellableProps } from '../types.js';
import type { TextStyleOptions } from '../typography/types.js';
import { textStyle } from '../typography/utils.js';

export interface SpinnerProps extends CommonProps, LabellableProps {
  /**
   * Determines the size of the spinner.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
}

const ringSizes: Record<NonNullable<SpinnerProps['size']>, string> = {
  small: '24px',
  medium: '48px',
  large: '80px',
};

const labelVariants: Record<NonNullable<SpinnerProps['size']>, TextStyleOptions['variant']> = {
  small: 'small',
  medium: 'medium',
  large: 'body',
};

function Spinner({ size = 'medium', label, ...props }: SpinnerProps, ref: ForwardedRef<HTMLDivElement>) {
  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('Spinner requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  return (
    <InternalSpinner {...props} size={ringSizes[size]} spinnerRef={ref}>
      {label && <RACLabel className={textStyle({ variant: labelVariants[size], tone: 'base' })}>{label}</RACLabel>}
    </InternalSpinner>
  );
}

/**
 * Displays a loading indicator to represent an ongoing operation.
 *
 * See [spinner usage guidelines](https://ui.cimpress.io/components/spinner/).
 */
const _Spinner = withStyleProps(forwardRef(Spinner), 'Spinner');

export { _Spinner as Spinner };
